import React, { useState, useEffect, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LibraryAddTwoToneIcon from "@mui/icons-material/LibraryAddTwoTone";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import GroupsIcon from "@mui/icons-material/Groups";
import type { TodoListCreateDto } from "../hooks/useTodos.ts";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";

// 型定義（プロジェクトテンプレート用）
interface ProjectTemplate {
  projectTemplateId: number;
  templateName: string;
  roomId: number;
  isShare: boolean;
  createdHonbuId: number;
}

interface Props {
  honbuId: number;
  onClose: () => void;
  roomId?: number;
  // templateId を受け取れるように拡張することを想定
  onAddList: (listData: TodoListCreateDto & { templateId?: number }) => void;
  showCreatePrompt?: boolean;
  color: PaletteColor;
  onEditProjectTemplate: (templateId: number) => void;
}

export function ListCreateDialog({
  honbuId,
  onClose,
  roomId,
  onAddList,
  showCreatePrompt = false,
  color,
  onEditProjectTemplate,
}: Props) {
  const accent = PaletteColorHex[color];
  const [newListName, setNewListName] = useState("");
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [otherRoomTemplates, setOtherRoomTemplates] = useState<
    ProjectTemplate[]
  >([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 確認ダイアログ用のstate
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    templateId: number | null;
    templateName: string;
  }>({
    isOpen: false,
    templateId: null,
    templateName: "",
  });

  // 共有確認ダイアログ用のstate
  const [shareConfirmDialog, setShareConfirmDialog] = useState<{
    isOpen: boolean;
    templateId: number | null;
    templateName: string;
    currentIsShare: boolean;
  }>({
    isOpen: false,
    templateId: null,
    templateName: "",
    currentIsShare: false,
  });

  // --- テンプレート取得 ---
  useEffect(() => {
    const fetchTemplates = async () => {
      if (roomId === undefined) return;
      setIsLoading(true);
      try {
        // この部屋のテンプレート
        const res = await fetch(
          `/api/project-templates?roomId=${roomId}&honbuId=${honbuId}`,
        );
        if (res.ok) setTemplates(await res.json());

        // 他の部屋の自分のテンプレート
        const otherRes = await fetch(
          `/api/project-templates/other-rooms?honbuId=${honbuId}&currentRoomId=${roomId}`,
        );
        if (otherRes.ok) setOtherRoomTemplates(await otherRes.json());
      } catch (e) {
        console.error("Failed to fetch project templates", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplates();
  }, [roomId, honbuId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // --- 作成実行 ---
  const handleAddNewList = () => {
    if (!newListName.trim() || roomId === undefined) return;

    onAddList({
      name: newListName.trim(),
      roomId: roomId,
      createdHonbuId: honbuId,
      templateId: selectedTemplateId ?? undefined, // テンプレートが選ばれていれば渡す
    });

    setNewListName("");
    onClose();
  };

  // --- テンプレート削除確認ダイアログを開く ---
  const handleDeleteTemplate = (
    e: React.MouseEvent,
    id: number,
    name: string,
  ) => {
    e.stopPropagation();
    setConfirmDialog({
      isOpen: true,
      templateId: id,
      templateName: name,
    });
  };

  // --- テンプレート削除実行 ---
  const executeDeleteTemplate = async () => {
    if (!confirmDialog.templateId) return;

    try {
      const res = await fetch(
        `/api/project-templates/${confirmDialog.templateId}`,
        {
          method: "DELETE",
        },
      );
      if (res.ok) {
        setTemplates((prev) =>
          prev.filter((t) => t.projectTemplateId !== confirmDialog.templateId),
        );
        setOtherRoomTemplates((prev) =>
          prev.filter((t) => t.projectTemplateId !== confirmDialog.templateId),
        );
        if (selectedTemplateId === confirmDialog.templateId)
          setSelectedTemplateId(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setConfirmDialog({ isOpen: false, templateId: null, templateName: "" });
    }
  };

  // --- 共有トグル確認ダイアログを開く ---
  const handleToggleShareConfirm = (
    e: React.MouseEvent,
    template: ProjectTemplate,
  ) => {
    e.stopPropagation();
    setShareConfirmDialog({
      isOpen: true,
      templateId: template.projectTemplateId,
      templateName: template.templateName,
      currentIsShare: template.isShare,
    });
  };

  // --- 共有トグル実行 ---
  const executeToggleShare = async () => {
    if (!shareConfirmDialog.templateId) return;

    const newIsShare = !shareConfirmDialog.currentIsShare;
    try {
      const response = await fetch(
        `/api/project-templates/${shareConfirmDialog.templateId}/share?honbuId=${honbuId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ IsShare: newIsShare }),
        },
      );
      if (response.ok) {
        // テンプレートリストを更新
        setTemplates((current) =>
          current.map((t) =>
            t.projectTemplateId === shareConfirmDialog.templateId
              ? { ...t, isShare: newIsShare }
              : t,
          ),
        );
        setOtherRoomTemplates((current) =>
          current.map((t) =>
            t.projectTemplateId === shareConfirmDialog.templateId
              ? { ...t, isShare: newIsShare }
              : t,
          ),
        );
      } else {
        alert("共有設定の変更に失敗しました。");
      }
    } catch (error) {
      console.error("Failed to update share setting:", error);
      alert("エラーが発生しました。");
    } finally {
      setShareConfirmDialog({
        isOpen: false,
        templateId: null,
        templateName: "",
        currentIsShare: false,
      });
    }
  };

  const TemplateItem = ({
    t,
    isOther,
  }: {
    t: ProjectTemplate;
    isOther: boolean;
  }) => (
    <div
      key={t.projectTemplateId}
      className={`group p-3 border rounded-lg flex justify-between items-center transition-all ${
        selectedTemplateId === t.projectTemplateId
          ? "ring-1"
          : "hover:bg-gray-50 border-gray-200"
      }`}
      style={
        selectedTemplateId === t.projectTemplateId
          ? {
              borderColor: PaletteColorHex[color],
              backgroundColor: `${PaletteColorHex[color]}10`,
              boxShadow: `0 0 0 1px ${PaletteColorHex[color]}`,
            }
          : undefined
      }
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          style={{
            color:
              selectedTemplateId === t.projectTemplateId
                ? PaletteColorHex[color]
                : undefined,
          }}
          className={
            selectedTemplateId === t.projectTemplateId ? "" : "text-gray-400"
          }
        ></div>
        <div className="flex flex-col min-w-0">
          <span
            className={`text-sm font-medium truncate ${
              selectedTemplateId === t.projectTemplateId ? "" : "text-gray-700"
            }`}
            style={{
              color:
                selectedTemplateId === t.projectTemplateId
                  ? PaletteColorHex[color]
                  : undefined,
            }}
          >
            {t.templateName}
          </span>
          {isOther && (
            <span className="text-[10px] text-gray-400">
              他ルームのテンプレート
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        {/* 編集・削除（自分が作成者、または管理者権限がある場合を想定） */}
        {t.createdHonbuId === honbuId && (
          <div className="flex items-center opacity-100 transition-opacity">
            {roomId !== 0 && (
              <button
                onClick={(e) => handleToggleShareConfirm(e, t)}
                className={`p-1 transition-colors ${
                  t.isShare
                    ? "text-blue-500 hover:text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                title={t.isShare ? "グループで共有中" : "共有していません"}
              >
                {t.isShare ? (
                  <GroupsIcon fontSize="small" />
                ) : (
                  <LockIcon fontSize="small" />
                )}
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditProjectTemplate(t.projectTemplateId);
              }}
              className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
              title="編集"
            >
              <EditIcon fontSize="small" />
            </button>
            <button
              onClick={(e) =>
                handleDeleteTemplate(e, t.projectTemplateId, t.templateName)
              }
              className="p-1 text-gray-400 hover:text-red-500"
              title="削除"
            >
              <DeleteIcon fontSize="small" />
            </button>
          </div>
        )}
        <button
          onClick={() => {
            setSelectedTemplateId(t.projectTemplateId);
            if (!newListName.trim()) setNewListName(t.templateName);
          }}
          className={`px-3 py-1 rounded text-xs font-bold transition-colors ${
            selectedTemplateId === t.projectTemplateId
              ? "text-white"
              : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
          }`}
          style={
            selectedTemplateId === t.projectTemplateId
              ? { backgroundColor: PaletteColorHex[color] }
              : undefined
          }
        >
          {selectedTemplateId === t.projectTemplateId ? "選択中" : "使用"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* 確認ダイアログ（削除） */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="テンプレートの削除"
        message={`「${confirmDialog.templateName}」を削除してもよろしいですか？\nこの操作は元に戻せません。`}
        confirmText="削除"
        type="danger"
        onConfirm={executeDeleteTemplate}
        onCancel={() =>
          setConfirmDialog({
            isOpen: false,
            templateId: null,
            templateName: "",
          })
        }
        color={color}
      />

      {/* 確認ダイアログ（共有） */}
      <ConfirmDialog
        isOpen={shareConfirmDialog.isOpen}
        title={shareConfirmDialog.currentIsShare ? "共有を解除" : "共有する"}
        message={
          shareConfirmDialog.currentIsShare
            ? `「${shareConfirmDialog.templateName}」の共有を解除しますか？`
            : `「${shareConfirmDialog.templateName}」を共有しますか？`
        }
        confirmText={shareConfirmDialog.currentIsShare ? "解除" : "共有"}
        type="normal"
        onConfirm={executeToggleShare}
        onCancel={() =>
          setShareConfirmDialog({
            isOpen: false,
            templateId: null,
            templateName: "",
            currentIsShare: false,
          })
        }
        color={color}
      />

      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 sm:p-4"
        onClick={onClose}
      >
        <div
          className="bg-white w-full h-full flex flex-col shadow-2xl border-2 border-white sm:rounded-xl sm:max-w-md sm:h-auto sm:max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
            }}
          >
            <div className="flex items-center gap-2 text-white">
              <LibraryAddTwoToneIcon fontSize="small" />
              <span className="font-bold text-sm">プロジェクトの新規作成</span>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-full text-white hover:bg-white/20"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto min-h-0 flex-1 sm:flex-initial">
            {showCreatePrompt && (
              <p className="p-3 text-sm text-center text-red-600 bg-red-50 border border-red-200 rounded-md">
                タスクを追加できるプロジェクトがありません。
                <br />
                まず新しいプロジェクトを作成してください。
              </p>
            )}

            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                プロジェクト名
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="例: 日常業務"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  className="flex-grow p-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-500 outline-none transition-all"
                  onKeyDown={(e) => e.key === "Enter" && handleAddNewList()}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  テンプレートを選択 (任意)
                </label>
                {selectedTemplateId && (
                  <button
                    onClick={() => setSelectedTemplateId(null)}
                    className="text-[10px] text-blue-500 hover:underline"
                  >
                    選択を解除
                  </button>
                )}
              </div>

              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1 ios-scroll">
                {isLoading ? (
                  <div className="text-center py-4 text-gray-400 text-sm italic">
                    読み込み中...
                  </div>
                ) : (
                  <>
                    {/* 現在の部屋 */}
                    {templates.map((t) => (
                      <TemplateItem
                        t={t}
                        isOther={false}
                        key={t.projectTemplateId}
                      />
                    ))}

                    {/* 他の部屋（自分が作ったもの） */}
                    {otherRoomTemplates.length > 0 && (
                      <>
                        <div className="pt-2 pb-1 text-[10px] font-bold text-gray-400 border-b">
                          他の部屋のテンプレート
                        </div>
                        {otherRoomTemplates.map((t) => (
                          <TemplateItem
                            t={t}
                            isOther={true}
                            key={t.projectTemplateId}
                          />
                        ))}
                      </>
                    )}

                    {templates.length === 0 &&
                      otherRoomTemplates.length === 0 && (
                        <div className="text-center py-8 border-2 border-dashed border-gray-100 rounded-lg text-gray-400 text-xs">
                          利用可能なテンプレートがありません
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-50 border-t flex gap-3 flex-shrink-0 sm:mt-0 mt-auto">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors"
            >
              キャンセル
            </button>
            <button
              onClick={handleAddNewList}
              disabled={!newListName.trim()}
              className="flex-1 px-4 py-2.5 text-white rounded-lg font-bold shadow-md hover:brightness-110 disabled:bg-gray-300 disabled:shadow-none transition-all"
              style={{ backgroundColor: PaletteColorHex[color] }}
            >
              プロジェクトを作成
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
